import React, { useEffect, useState } from 'react';
import {
    Grid,
    Box,
    Button,
    InputLabel,
    OutlinedInput,
    MenuItem,
    Checkbox,
    Select,
    FormControl,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { gridSpacing } from 'utils/constants';
import ApiHelper from 'utils/apiHelper';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import DeleteConfirmationDialog from 'components/Management/DeleteConfirmationDialog';
import AddTemplateDialog from './AddTemplateDialog';
import EditTemplateDialog from './EditTemplateDialog';

const ManagementNotifications = () => {
    const { userInfo } = useSelector((state) => state.auth);
    const manageOrganizationData = useSelector((state) => state.management?.organization.selected);
    const initialState = {
        cc: '',
        bcc: '',
        subject: '',
        additionalText: ''
    };
    const [{ cc, bcc, subject, additionalText }, setState] = useState(initialState);
    const [recipients, setRecipients] = useState([]);
    const [organizationsData, setOrganizationsData] = useState([]);
    const [templatesData, setTemplatesData] = useState([]);
    const [template, setTemplate] = useState('0');
    const [templateBody, setTemplateBody] = useState('');
    const [showBodyField, setShowBodyField] = useState(false);
    const [showAddTemplate, setShowAddTemplate] = useState(false);
    const [showEditTemplate, setShowEditTemplate] = useState(false);
    const [selectedTemplateData, setSelectedTemplateData] = useState();
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [deleteTemplateId, setDeleteTemplateId] = useState();
    const [loading, setLoading] = useState(false);
    const [limitFields, setLimitFields] = useState({});

    useEffect(() => {
        getOrganizations();
        getTemplates();
    }, []);

    useEffect(() => {
        if (template !== '0' && template !== '') {
            setShowBodyField(true);
            const templateId = templatesData.findIndex((data) => data.id === template);
            setTemplateBody(templatesData[templateId].body);
        } else {
            setShowBodyField(false);
        }
    }, [template, templatesData]);

    const getOrganizations = () => {
        ApiHelper.get('/api/management/organizations/')
            .then((res) => {
                setOrganizationsData(res.data);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error('Failed to get the organizations data');
                }
            });
    };

    const getTemplates = () => {
        ApiHelper.get('/api/management/templates/')
            .then((res) => {
                setTemplatesData(res.data.template_data);
                setLimitFields(res.data.limitFields);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error('Failed to get the templates data');
                }
            });
    };

    const handleChangeInput = (e) => {
        const { name, value } = e.target;
        setState((prevState) => ({ ...prevState, [name]: value }));
    };

    const clearState = () => {
        setState({ ...initialState });
    };

    const handleChangeUserGroupId = (event) => {
        const {
            target: { value }
        } = event;
        setRecipients(typeof value === 'string' ? value.split(',') : value);
    };

    const handleClose = () => {
        setShowDeleteConfirmationDialog(false);
    };

    const handleCloseAddTemplate = () => {
        setShowAddTemplate(false);
        getTemplates();
    };

    const handleCloseEditTemplate = () => {
        setShowEditTemplate(false);
        getTemplates();
    };

    const handleEditTemplate = (data) => {
        setShowEditTemplate(true);
        setSelectedTemplateData(data);
    };

    const handleDeleteConfirmation = (id) => {
        setShowDeleteConfirmationDialog(true);
        setDeleteTemplateId(id);
    };

    const handleDelete = () => {
        ApiHelper.delete(`/api/management/templates/${deleteTemplateId}/`)
            .then(() => {
                toast.success('Successfully deleted the template');
                getTemplates();
                handleClose();
            })
            .catch((err) => {
                toast.error(`Fail! ${err.response.data.message}`);
                handleClose();
            });
    };

    const handleSubmit = () => {
        if (recipients.length === 0 && cc === '') {
            toast.warning('Please select the Recipients, or fill the CC');
            return true;
        }
        if (subject === '') {
            toast.warning('Please fill the Subject');
            return true;
        }
        if (templateBody === '' && additionalText === '') {
            toast.warning('Please select a template or write the additional text');
            return true;
        }
        setLoading(true);
        ApiHelper.post('/api/management/notifications/', {
            recipients: recipients,
            cc: cc,
            bcc: bcc,
            subject: subject,
            template_id: template,
            template_body: templateBody,
            additional_text: additionalText
        })
            .then(() => {
                toast.success('Successfully sent');
                setLoading(false);
            })
            .catch((err) => {
                if (err.response.data.message !== '') {
                    toast.error(`Failed! ${err.response.data.message}`);
                } else {
                    toast.error('Failed to send the email');
                }
                setLoading(false);
            });
    };

    return (
        <MainCard
            title="Notifications Management"
            cardHeaderShow={true}
            cardHeaderTitle={manageOrganizationData?.orgid ? manageOrganizationData?.name : userInfo.user.orgid.name}
        >
            <Grid container spacing={gridSpacing}>
                <Grid item xs={4}>
                    <SubCard title="Send Email">
                        <Grid container spacing={gridSpacing}>
                            <Grid item xs={12}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Recipients</InputLabel>
                                    <Select
                                        value={recipients}
                                        multiple
                                        input={<OutlinedInput label="Recipients" />}
                                        onChange={handleChangeUserGroupId}
                                        renderValue={(selected) => selected.join(', ')}
                                    >
                                        {organizationsData.map((data, key) => (
                                            <MenuItem value={data.name} key={key}>
                                                <Checkbox checked={recipients.indexOf(data.name) > -1} />
                                                {data.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField sx={{ width: '100%' }} label="CC" value={cc} name="cc" onChange={handleChangeInput} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField sx={{ width: '100%' }} label="BCC" value={bcc} name="bcc" onChange={handleChangeInput} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    sx={{ width: '100%' }}
                                    label="Subject"
                                    value={subject}
                                    name="subject"
                                    onChange={handleChangeInput}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select the template</InputLabel>
                                    <Select label="Select the template" value={template} onChange={(e) => setTemplate(e.target.value)}>
                                        <MenuItem value={'0'}>None</MenuItem>
                                        {templatesData.map((data, key) => (
                                            <MenuItem key={key} value={data.id}>
                                                {data.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {showBodyField && (
                                <Grid item xs={12}>
                                    <TextField
                                        sx={{ width: '100%' }}
                                        label="Body"
                                        value={templateBody}
                                        disabled={true}
                                        multiline={true}
                                        rows={5}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <TextField
                                    sx={{ width: '100%' }}
                                    label="Additional Text"
                                    value={additionalText}
                                    name="additionalText"
                                    onChange={handleChangeInput}
                                />
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <Box sx={{ float: 'right' }}>
                                    <Button sx={{ marginRight: 2 }} onClick={clearState}>
                                        Cancel
                                    </Button>
                                    <LoadingButton loading={loading} onClick={handleSubmit} color="primary" variant="contained">
                                        Send
                                    </LoadingButton>
                                </Box>
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
                <Grid item xs={4}>
                    <SubCard title="Email Templates">
                        <Grid container spacing={gridSpacing}>
                            <Grid item xs={12}>
                                <Button
                                    sx={{ float: 'right', borderRadius: '6px' }}
                                    onClick={() => setShowAddTemplate(true)}
                                    startIcon={<Add />}
                                    color="primary"
                                    variant="contained"
                                >
                                    Add Template
                                </Button>
                                {showAddTemplate && (
                                    <AddTemplateDialog
                                        onClose={handleCloseAddTemplate}
                                        onCloseCancel={() => setShowAddTemplate(false)}
                                        limitFields={limitFields}
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Template Name</TableCell>
                                            <TableCell>Description</TableCell>
                                            <TableCell align="right">Options</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {templatesData.map((data, key) => (
                                            <TableRow key={key}>
                                                <TableCell>{data.name}</TableCell>
                                                <TableCell>{data.description}</TableCell>
                                                <TableCell align="right">
                                                    <Box>
                                                        <IconButton size="small" onClick={() => handleEditTemplate(data)}>
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" onClick={() => handleDeleteConfirmation(data.id)}>
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {showEditTemplate && (
                                            <EditTemplateDialog
                                                onCloseCancel={() => setShowEditTemplate(false)}
                                                templateData={selectedTemplateData}
                                                onClose={handleCloseEditTemplate}
                                                limitFields={limitFields}
                                            />
                                        )}
                                        {showDeleteConfirmationDialog && (
                                            <DeleteConfirmationDialog onClose={handleClose} onYes={handleDelete} name="template" />
                                        )}
                                    </TableBody>
                                </Table>
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
            </Grid>
        </MainCard>
    );
};

export default ManagementNotifications;
